import { inject, PLATFORM_ID } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateChildFn, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { ApiService } from '../services/api.service';
import { JWT_CHECK_API_RESPONSE, USERINFO } from '../models/service';
import { catchError, map, Observable, of, ReplaySubject, take } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { SignalRService } from '../services/signalr.service';

export interface IAuthGuard { 
  IsOk:boolean,
  User:USERINFO
}

const isUserLoggedIn = (): Observable<IAuthGuard> => {
  const signalrService = inject(SignalRService);
  const loginService = inject(ApiService);
  const userLoggedIn: ReplaySubject<IAuthGuard> = new ReplaySubject<IAuthGuard>; 
  loginService.requestGet('authorization/me').subscribe({
    next:(tokenResponse:JWT_CHECK_API_RESPONSE | any)=>{
      const profileData = tokenResponse.dataobject || <USERINFO>{};
      const isTokenOk = (tokenResponse && tokenResponse.hasOwnProperty('status') && tokenResponse.status.toString()=='200') ? true : false;
      const EntityTypeID = profileData.EntityTypeID || 0;
      const isValidUserType = ![1,2,4].includes(EntityTypeID) ? true : false;
      const canAccess = isTokenOk && isValidUserType ? true : false;

      if(!canAccess){
        loginService.updateProfile(<USERINFO>{});      
        loginService.logout(false);   
      }else{
        //Connect SignalR Services
        signalrService.connect();          
        loginService.updateProfile(profileData);     
      }      
      loginService.updateLoginStatus(canAccess);        
      //console.log('profileData', profileData)
      const AuthResult :IAuthGuard = {IsOk :canAccess, User:profileData } as IAuthGuard;
      userLoggedIn.next(AuthResult)

    },
    error:()=>{
      loginService.updateProfile(<USERINFO>{});
      loginService.updateLoginStatus(false);
      const AuthResult :IAuthGuard = {IsOk :false, User:<USERINFO>{} } as IAuthGuard;
      //Disconnect SignalR Services
      signalrService.disconnect();  
      userLoggedIn.next(AuthResult)
    }
  });  
  return userLoggedIn.asObservable();
};

export const refreshToken: CanActivateFn = (route, state) => {  
  const platformId = inject(PLATFORM_ID);
  if (!isPlatformBrowser(platformId)) {
    return true;
  }
  return isUserLoggedIn().pipe(
    map((userInfo: IAuthGuard) => {             
      return true;
    })
  );
};

export const TokenParamRequired: CanActivateFn = (route, state) => {  
  const platformId = inject(PLATFORM_ID);
  if (!isPlatformBrowser(platformId)) {
    return true;
  }  
  const router = inject(Router);
  const routeDataObj = route.data || {};
  const paramKeyName = routeDataObj['keyname'] || 'accessToken';
  const redirectionRoute = routeDataObj['redirection'] || 'login';  
  const queryParam = route.paramMap.get(paramKeyName);  
  if(!queryParam){
    return router.createUrlTree(['/', redirectionRoute]); 
  }
  return true;

};


export const canLoginOrRegisterGuard: CanActivateFn = (route, state) => {
  const router = inject(Router);
  const platformId = inject(PLATFORM_ID);
  if (!isPlatformBrowser(platformId)) {
    return true;
  }

  return isUserLoggedIn().pipe(   
    map((AuthResponse: IAuthGuard)=>{      
      if(AuthResponse && AuthResponse.IsOk){
        return router.createUrlTree(['/home']);
      }
      return true;
    }),
    catchError(()=>{
      return of(true);
    })
  );
};

export const canActiveChildRoute: CanActivateChildFn = (childRoute:ActivatedRouteSnapshot, state:RouterStateSnapshot) => { 
  const router = inject(Router);
  const platformId = inject(PLATFORM_ID);
  if (!isPlatformBrowser(platformId)) {
    return true;
  }
  return isUserLoggedIn().pipe(   
    map((AuthResponse:IAuthGuard)=>{      
      if(!AuthResponse.IsOk){
        return router.createUrlTree(['/home']);
      }
      return true;
    }),
    catchError(() => of(router.createUrlTree(['/home'])))
  );
}

export function canAccessChildRoute(EntityTypeID: number): CanActivateChildFn {
  return (childRoute:ActivatedRouteSnapshot, state:RouterStateSnapshot) => {    
    const router = inject(Router);
    const platformId = inject(PLATFORM_ID);
    if (!isPlatformBrowser(platformId)) {
      return true;
    }
    return isUserLoggedIn().pipe(   
      map((AuthResponse:IAuthGuard)=>{  
        const UserEntityTypeID:string =  (AuthResponse.User &&  AuthResponse.User.EntityTypeID ?   AuthResponse.User.EntityTypeID : 0).toString();
        const InputEntityTypeID:string =  (EntityTypeID && EntityTypeID>0 ? EntityTypeID : 9999).toString();
        //console.log('UserEntityTypeID', UserEntityTypeID,'InputEntityTypeID', InputEntityTypeID );
        if(!AuthResponse.IsOk || UserEntityTypeID!==InputEntityTypeID){
          return router.createUrlTree(['/home']);
        }
        return true;
      }),
      catchError(() => of(router.createUrlTree(['/home'])))
    );
  };
}


